import { CreatePowerPointTableParams } from '../Model';
import { ExportContext } from '@utils/pageConfig/Types';
import { getContextPowerPoint, getDateTime } from '../ExportUtils';
import { ImageURLData } from '@ecosystem/export-utils';
import { saveFile } from '../SaveFile';
import {
  AvailableTemplate,
  ColumnType,
  DataCell,
  DataTable,
  PowerpointExportDto,
  PowerpointSlide,
  PowerpointSlideType,
  TableColumn,
} from '@ecosystem/export-api';
import { Feature, ItemFeaturesResp, ProductsCompareKpi, ProductsCompareKpiItem } from '@models/Products';
import { getPowerPointSlide } from '../ExportChartPowerPoint';
import { COMPARE_MODEL } from '@utils/StringConstants';
import { TOTAL_ITEM_NAME, FEATURE_UNAVAILABLE } from '@components/products-compare-modal/Types';
import {
  getFeatureNames,
  getProcessedFeatures,
} from '@components/products-compare-modal/utils/ProductsModelCompareUtils';
import { processExportedImages } from '@hooks/export/ExportChart';

export const createChartAndTable = async (methodParam: CreatePowerPointTableParams): Promise<void> => {
  let imageDataList: (ImageURLData | undefined)[] = [];

  if (Array.isArray(methodParam?.multipleChartsExport) && methodParam.multipleChartsExport.length) {
    const invokeExportsResultList = await Promise.all(
      methodParam.multipleChartsExport.map((exp) => exp.invokeExport?.()),
    );
    imageDataList = invokeExportsResultList.filter(Boolean);

    if (!imageDataList.length) {
      console.warn('No export image data retrieved from multipleChartsExport');
    }
  }

  const exportedPngImages = await processExportedImages(methodParam.exportImage);

  const params: CreatePowerPointTableParams = {
    ...methodParam,
    imageData: imageDataList.length ? imageDataList : exportedPngImages,
  };

  const exportData = getImageAndTable(params);
  const exportFile = await methodParam.exportFn(exportData);
  saveFile(exportFile);
};

// to create PowerPoint export data object for export
export const getImageAndTable = (params: CreatePowerPointTableParams): PowerpointExportDto => {
  const exportContext: ExportContext = {
    appliedCountry: params.appliedCountry,
    appliedPg: params.appliedPg,
    timePeriod: params.timePeriod,
    comparisonTimePeriod: params.comparisonTimePeriod,
    periodType: params.periodType,
    showPerformanceWithin: '',
    appliedFiltersText: params.appliedFiltersText,
    pageName: params.pageName,
  };

  const slides: PowerpointSlide[] = params?.imageData?.map((image, index) =>
    getPowerPointSlide(image, params.chartTitles[index], params.chartSubTitles[index]),
  );

  slides.push(...getTableSlides(params.featureData, params.currency));

  return {
    template: AvailableTemplate.Powerpoint,
    name: params.filename,
    userDateTimeIso8601: getDateTime(),
    context: getContextPowerPoint(exportContext),
    slides: slides,
  };
};

// to generate table column for table in PowerPoint
const getFeatureTableColumn = (items: ProductsCompareKpiItem[]): TableColumn[] => {
  const emptyObject = {
    header: '',
    type: ColumnType.Text,
  };
  return [
    emptyObject,
    ...items
      .filter((item) => TOTAL_ITEM_NAME !== item?.item?.itemName)
      .map((kpiItem: ProductsCompareKpiItem) => ({
        header: `${kpiItem.item.itemBrand} ${kpiItem.item.itemName}`,
        type: ColumnType.Text,
      })),
  ];
};

// to generate table row for table in PowerPoint
const getFeatureTableRow = (productsCompareKpi: ProductsCompareKpi): DataCell[][] => {
  const firstActivityHeader: DataCell = { value: 'First activity', type: ColumnType.Text };
  const firstActivityRow: DataCell[] = [
    firstActivityHeader,
    ...productsCompareKpi.items
      .filter((item: ProductsCompareKpiItem) => TOTAL_ITEM_NAME !== item?.item?.itemName)
      .map((item: ProductsCompareKpiItem) => ({
        value: item.item.firstActivityDate,
        type: ColumnType.Text,
      })),
  ];

  const dataRows: DataCell[][] = [firstActivityRow];
  const itemIds: string[] = productsCompareKpi.items
    .filter((item: ProductsCompareKpiItem) => TOTAL_ITEM_NAME !== item?.item?.itemName)
    .map((item: ProductsCompareKpiItem) => item.item.itemId);
  const featureMap: Map<string, string[]> = new Map<string, string[]>();
  const [itemFeatures] = productsCompareKpi.itemFeatures.map((itemFeature) => itemFeature.features);

  const featureNames = getFeatureNames(productsCompareKpi).sort((a, b) => a.localeCompare(b));
  const processedFeatures = getProcessedFeatures(productsCompareKpi);

  if (!itemFeatures || featureNames.length <= 0) {
    return [];
  }
  // to set key value pair key for feature name without feature value
  featureNames.forEach((featureName) => {
    if (featureName && featureName.length > 0) {
      featureMap.set(featureName, []);
    }
  });

  transformFeatures(itemIds, featureMap, productsCompareKpi.itemFeatures, processedFeatures);
  return loadDataRows(featureMap, dataRows);
};

// to load and transform feature and feature value into feature map
const transformFeatures = (
  itemIds: string[],
  featureMap: Map<string, string[]>,
  itemFeatures: ItemFeaturesResp[],
  processedFeatures: Map<string, Map<string | null, string | null>>,
): void => {
  const featureNames = Array.from(featureMap.keys());
  itemIds.forEach((itemId) => {
    const processedFeature = processedFeatures.get(itemId);
    const compareFeatures = itemFeatures
      .filter((itemFeaturesResp: ItemFeaturesResp) => itemFeaturesResp.itemId === itemId)
      .flatMap((itemCompareFeature) => itemCompareFeature?.features ?? []);

    // get new compare features
    const newCompareFeatures: Feature[] = featureNames.map((featureName) => {
      const feature = compareFeatures.find((compareFeature) => compareFeature.name === featureName);

      let featureValue = FEATURE_UNAVAILABLE;

      // Display a blank only if there is at least 1 feature for
      // a given item. Otherwise, there is currently no available
      // features
      if (processedFeature && processedFeature.size > 0) {
        featureValue = feature?.value ?? '';
      }

      return {
        name: featureName,
        value: featureValue,
      };
    });

    loadFeatureValue(featureMap, newCompareFeatures);
  });
};

// to load text for feature value
const loadFeatureValue = (featureMap: Map<string, string[]>, compareFeatures: Feature[]): void => {
  featureMap.forEach((value, key) => {
    const itemFeature = compareFeatures.find((feature) => feature.name === key);
    const featureName = itemFeature?.name ?? '';
    const featureValue = itemFeature?.value ?? '';

    const featuresNames = featureMap.get(featureName) || [];
    featureMap.set(key, [...featuresNames, featureValue]);
  });
};

// to load and transform feature map into data rows for table in PowerPoint
const loadDataRows = (featureMap: Map<string, string[]>, dataRows: DataCell[][]): DataCell[][] => {
  featureMap.forEach((values, key) => {
    const dataRow: DataCell[] = [
      { value: key, type: ColumnType.Text },
      ...values.map((value) => ({ value: value, type: ColumnType.Text })),
    ];
    dataRows.push(dataRow);
  });

  return dataRows;
};

const getTableSlides = (productsCompareKpi: ProductsCompareKpi, currency: string): PowerpointSlide[] => {
  const dataRows: DataCell[][] = getFeatureTableRow(productsCompareKpi);
  if (dataRows.length <= 0) {
    return [];
  }
  const dataTable: DataTable = {
    currencySymbol: currency,
    dataColumns: getFeatureTableColumn(productsCompareKpi.items),
    dataRows: dataRows,
  };
  return [
    {
      type: PowerpointSlideType.Table,
      title: COMPARE_MODEL,
      subtitle: 'Features',
      table: dataTable,
    },
  ];
};
